import React from 'react';
import type { Invoice } from '@hero/schema';
import type { InvoiceUnifiedStatus } from '@hero/api';
import { useTranslation } from 'react-i18next';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import { TableEmptyState } from '../../../app/Table';
import type { SearchInputProps } from '../../../app/Search';
import { InvoiceRow } from './InvoiceRow';
import { useInvoiceTableColumns } from '@hero/hooks-shared';
import type { InvoiceTableColumn } from '@hero/hooks-shared';
import { getInvoiceStatusBadgeClass } from '@hero/web-theme';
import { InvoiceIcon, InvoiceStatusDynamicIcon } from '@hero/web-icons';
export type { InvoiceTableColumn };

// ─── Filter configuration types ──────────────────────────────────────────────

export interface InvoiceStatusFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  /** Static list of filter options (defines which badges are visible) */
  options: Array<{
    value: string;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    className?: string;
  }>;
  /** Dynamic counts array (updates frequently, takes priority over option.count) */
  counts?: Array<{ value: string; count: number }>;
}

export interface InvoiceTableFilters {
  showSearch?: boolean;
  statusFilter?: InvoiceStatusFilterConfig;
}

// ─── Table props ─────────────────────────────────────────────────────────────

export interface InvoiceTableProps {
  /** Invoice list data */
  invoices: Invoice[];
  /** Whether to render for customer or carrier */
  displayForUser: 'customer' | 'carrier';
  /** Whether data is loading */
  isLoading?: boolean;
  /** Current sort column */
  sortColumn?: string;
  /** Current sort direction */
  sortDirection?: 'asc' | 'desc';
  /** Search props (controlled) */
  searchProps?: SearchInputProps;
  /** Sort handler */
  onSort?: (column: string) => void;
  /** Infinite scroll: load more */
  onLoadMore?: () => void;
  /** Infinite scroll: has more pages */
  hasNextPage?: boolean;
  /** Infinite scroll: is fetching next page */
  isFetchingNextPage?: boolean;
  /** Whether to show days-left on the due column */
  showDaysLeft?: boolean;
  /** Called when the pay action button is clicked */
  onPayInvoice?: (invoice: Invoice) => void;
  /** Called when the upload proof action is triggered (carrier only) */
  onUploadProof?: (invoice: Invoice) => void;
  /**
   * Called when the per-row "Finance" chip is clicked (carrier
   * only). Manager wires it to navigate to the financing apply
   * form with the invoice pre-selected.
   */
  onFinanceInvoice?: (invoice: Invoice) => void;
  /** Custom columns (if not provided, role defaults are used) */
  columns?: InvoiceTableColumn[];
  /** Simple filters configuration (optional) */
  filters?: InvoiceTableFilters;
  /** Status badge config for styling */
  statusConfig?: StatusBadgeConfigType;
  /** Whether to include actions column */
  includeActions?: boolean;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  /** Children — for advanced slot usage (search/badge overrides) */
  children?: React.ReactNode;
  /** Drawer props — hook for fetching a single invoice */
  drawerProps?: {
    useInvoiceOrders?: (invoiceId: string) => {
      orders: any[];
      isLoading: boolean;
      hasNextPage?: boolean;
      fetchNextPage?: () => void;
      isFetchingNextPage?: boolean;
    };
  };
}

/**
 * InvoiceTable — shared table component for invoices, matching OrderTable architecture.
 *
 * Renders invoice data with dynamic columns, status badges, and optional drawer.
 */
export const InvoiceTableComponent: React.FC<InvoiceTableProps> = ({
  invoices,
  displayForUser,
  isLoading,
  sortColumn,
  sortDirection,
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  showDaysLeft = false,
  onPayInvoice,
  onUploadProof,
  onFinanceInvoice,
  columns: customColumns,
  filters,
  statusConfig,
  includeActions = true,
  emptyState,
  hasActiveFilters,
  children,
  drawerProps,
}) => {
  const { t } = useTranslation('m-payment');

  // Process columns (auto-translate labels, add defaults)
  const { columns: processedColumns } = useInvoiceTableColumns({
    displayForUser,
    customColumns,
    includeActions,
  });

  // Default status badge config — uses centralized colors + icons from @hero/web-theme and @hero/web-icons
  const defaultStatusConfig: StatusBadgeConfigType = {
    getStatusClass: (status: string) => getInvoiceStatusBadgeClass(status as InvoiceUnifiedStatus),
    getStatusDisplayText: (status: string) => {
      return t(`invoice.status.${status}` as any) || status;
    },
    showIcon: true,
    getStatusIcon: (status: string) => (
      <InvoiceStatusDynamicIcon status={status as InvoiceUnifiedStatus} className="!h-4 !w-4" />
    ),
  };

  const appliedStatusConfig = statusConfig || defaultStatusConfig;

  const defaultEmptyState = emptyState ?? (
    <TableEmptyState
      icon={<InvoiceIcon className="h-7 w-7" />}
      title={t('invoice.table.empty.title', { defaultValue: 'No invoices yet' })}
      description={t('invoice.table.empty.description', {
        defaultValue: 'Invoices for your orders will appear here once they are generated.',
      })}
    />
  );

  return (
    <SharedTable
      data={invoices}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={appliedStatusConfig}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={defaultEmptyState}
      hasActiveFilters={hasActiveFilters}>
      {/* Render custom children OR built-in filters */}
      {children || <InvoiceTableHead filters={filters} />}

      {/* Always render table rows */}
      <SharedTable.Mid>
        <SharedTable.Rows
          columns={processedColumns}
          rowRender={(invoice: Invoice) => (
            <InvoiceRow
              invoice={invoice}
              displayForUser={displayForUser}
              columns={processedColumns}
              showDaysLeft={showDaysLeft}
              onPayInvoice={onPayInvoice}
              onUploadProof={onUploadProof}
              onFinanceInvoice={onFinanceInvoice}
            />
          )}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

// ─── InvoiceTableHead — built-in filter rendering ────────────────────────────

interface InvoiceTableHeadProps {
  filters?: InvoiceTableFilters;
}

const InvoiceTableHead: React.FC<InvoiceTableHeadProps> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.statusFilter && (
        <SharedTable.Badge
          options={filters.statusFilter.options}
          value={filters.statusFilter.value}
          onChange={filters.statusFilter.onChange}
          showCounts={false}
        />
      )}
    </SharedTable.Head>
  );
};

// Create InvoiceTable with sub-components for advanced usage
export const InvoiceTable = Object.assign(InvoiceTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});

// Export InvoiceRow for custom table implementations
export { InvoiceRow };
